import React, { useEffect, useState, useRef } from 'react'
import classNames from 'classnames'
import { VideoCardProps } from './VideoCard.d'
import Description from '@toasttab/shared/src/components/atoms/Description/Description'
import BrollVideo from '@toasttab/shared/src/components/atoms/Broll/BrollVideo'
import { resolvePrefix } from '@toasttab/shared/src/utils/assetUtils'
import useWindowSize from '@toasttab/shared/src/hooks/useWindowSize'

const VideoCard = ({
  badgeColor,
  logo,
  rxType,
  image,
  video,
  href,
  classes,
  heapInteraction,
  webm
}: VideoCardProps) => {
  const { width } = useWindowSize()
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isInView, setIsInView] = useState(false)
  const [videoLoaded, setVideoLoaded] = useState(false)

  const isMobile = width < 768

  useEffect(() => {
    if (!isMobile && videoRef.current) {
      let observer: IntersectionObserver | null = null

      // Dynamically import IntersectionObserver logic only when not mobile
      const setupObserver = async () => {
        if ('IntersectionObserver' in window && videoRef.current) {
          observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
              setIsInView(true)
              // Once video is in view, we can stop observing
              if (observer) {
                observer.disconnect()
              }
            }
          })
          observer.observe(videoRef.current)
        }
      }

      setupObserver()

      return () => {
        if (observer) {
          observer.disconnect()
        }
      }
    }
  }, [isMobile])

  useEffect(() => {
    if (!isMobile && isInView && !videoLoaded) {
      setVideoLoaded(true)
    }
  }, [isMobile, isInView, videoLoaded])

  useEffect(() => {
    if (!isMobile && videoLoaded && videoRef.current) {
      // Play video once it's loaded
      videoRef.current.play()
    }
  }, [isMobile, videoLoaded])

  const videoCardClasses = classNames(classes, {
    'flex bg-gray-30 rounded-lg overflow-hidden relative w-full h-full max-h-[420px] group':
      true
  })
  const cardBadgeColor = classNames(
    'py-1 px-2 inline-block mx-auto rounded-badge',
    {
      'bg-white': !badgeColor,
      'bg-yellow-25': badgeColor === 'yellow'
    }
  )

  return (
    <a
      className='rounded-lg overflow-hidden'
      data-interaction={heapInteraction ?? ''}
      href={href}
    >
      <div className={videoCardClasses}>
        {video && !isMobile ? (
          <div className='border-box rounded-lg overflow-hidden relative min-w-[264px] w-full min-h-[420px] h-full relative z-0'>
            <BrollVideo
              loaded={videoLoaded}
              videoName={resolvePrefix(video)}
              videoRef={videoRef}
              webm={webm}
            />
          </div>
        ) : (
          ''
        )}
        {image && isMobile ? (
          <picture>
            <source
              sizes='(max-width: 768px) 100vw, 50vw'
              srcSet={`${image}?fm=avif&w=264&fit=scale 264w, ${image}?fm=avif&w=528&fit=scale 528w`}
              type='image/avif'
            />
            <source
              sizes='(max-width: 768px) 100vw, 50vw'
              srcSet={`${image}?fm=webp&w=264&fit=scale 264w, ${image}?fm=webp&w=528&fit=scale 528w`}
              type='image/webp'
            />
            <img
              alt={`${video}-video`}
              className='h-full w-full object-cover object-center lazyload'
            />
          </picture>
        ) : (
          ''
        )}
        <div className='gradient h-full w-full absolute top-0 bottom-0 right-0 left-0 bg-hp-video-card-gradient z-20 rounded-lg' />
        <div className='absolute top-0 bottom-0 right-0 left-0 flex flex-col justify-end z-30'>
          <div className='px-10 pb-6 z-30 text-center'>
            {logo ? (
              <img
                alt={video}
                className='lazyload mb-4 mx-auto group-hover:-translate-y-2  transition-transform duration-100 ease-in'
                height='96'
                src={logo}
                width='184'
              />
            ) : (
              ''
            )}
            <div className={cardBadgeColor}>
              <Description variation='badge'>{rxType}</Description>
            </div>
          </div>
        </div>
      </div>
    </a>
  )
}

export default VideoCard
